import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API, withCredentials: true });

export const resolveImage = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const fetchSongs = (category) =>
  http.get("/songs", { params: category && category !== "All" ? { category } : {} }).then((r) => r.data);
export const fetchStories = () => http.get("/stories").then((r) => r.data);
export const fetchActivities = () => http.get("/activities").then((r) => r.data);
export const fetchPosts = () => http.get("/posts").then((r) => r.data);
export const reactToPost = (postId, sticker) =>
  http.post(`/posts/${postId}/react`, { sticker }).then((r) => r.data);
export const createPost = (payload) => http.post("/posts", payload).then((r) => r.data);
export const deletePost = (postId) => http.delete(`/posts/${postId}`).then((r) => r.data);
export const joinWaitlist = (payload) => http.post("/waitlist", payload).then((r) => r.data);
export const fetchWaitlistCount = () => http.get("/waitlist/count").then((r) => r.data.count);

export const authMe = () => http.get("/auth/me").then((r) => r.data);
export const authRegister = (payload) => http.post("/auth/register", payload).then((r) => r.data);
export const authLogin = (payload) => http.post("/auth/login", payload).then((r) => r.data);
export const authLogout = () => http.post("/auth/logout").then((r) => r.data);
export const subscribePack = (payload) => http.post("/pack/subscribe", payload).then((r) => r.data);
export const fetchPackSubscribers = () => http.get("/pack/subscribers").then((r) => r.data);
export const fetchPackArt = () => http.get("/pack/art").then((r) => r.data);
export const uploadPackArt = (day, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post(`/pack/art/${day}`, fd).then((r) => r.data);
};
export const deletePackArt = (day) => http.delete(`/pack/art/${day}`).then((r) => r.data);
export const sendContact = (payload) => http.post("/contact", payload).then((r) => r.data);
export const fetchContactMessages = () => http.get("/contact/messages").then((r) => r.data);
export const fetchPackAnalytics = () => http.get("/pack/analytics").then((r) => r.data);
export const replaceSongAudio = (songId, path) => {
  const fd = new FormData();
  fd.append("path", path);
  return http.post(`/admin/songs/${songId}/replace-audio`, fd).then((r) => r.data);
};

// Chunked upload — large files go up in 512KB slices so the proxy can't truncate them.
export const uploadChunked = async (file, onProgress) => {
  const CHUNK = 512 * 1024;
  const { upload_id } = await http
    .post("/uploads/chunked/init", {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      total_size: file.size,
    })
    .then((r) => r.data);
  const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i += 1) {
    const slice = file.slice(i * CHUNK, (i + 1) * CHUNK);
    await http.post(`/uploads/chunked/${upload_id}/chunk?index=${i}`, slice, {
      headers: { "Content-Type": "application/octet-stream" },
    });
    if (onProgress) onProgress(Math.round(((i + 1) / total) * 100));
  }
  return http.post(`/uploads/chunked/${upload_id}/complete`).then((r) => r.data);
};
export const googleSession = (session_id, invite_code) =>
  http.post("/auth/google/session", { session_id, invite_code: invite_code || "" }).then((r) => r.data);
export const updateProfile = (payload) => http.patch("/auth/me", payload).then((r) => r.data);
export const addFavourite = (songId) =>
  http.post(`/auth/me/favourites/${songId}`).then((r) => r.data.favourites);
export const removeFavourite = (songId) =>
  http.delete(`/auth/me/favourites/${songId}`).then((r) => r.data.favourites);

export const createCheckout = (lookup_key) =>
  http.post("/payments/checkout", { lookup_key, origin_url: window.location.origin }).then((r) => r.data);
export const fetchPaymentStatus = (sessionId) =>
  http.get(`/payments/status/${sessionId}`).then((r) => r.data);

export const uploadPhoto = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post("/uploads", fd).then((r) => r.data);
};

export const fetchPendingPosts = () => http.get("/admin/posts/pending").then((r) => r.data);
export const moderatePost = (postId, action) =>
  http.post(`/admin/posts/${postId}/moderate`, { action }).then((r) => r.data);
export const fetchAdminStats = () => http.get("/admin/stats").then((r) => r.data);

export const addComment = (postId, text) =>
  http.post(`/posts/${postId}/comments`, { text }).then((r) => r.data);
export const deleteComment = (postId, commentId) =>
  http.delete(`/posts/${postId}/comments/${commentId}`).then((r) => r.data);
export const reportTarget = (payload) => http.post("/reports", payload).then((r) => r.data);
export const blockUser = (userId) => http.post(`/users/${userId}/block`).then((r) => r.data.blocked);
export const unblockUser = (userId) => http.delete(`/users/${userId}/block`).then((r) => r.data.blocked);

export const fetchNotifications = () => http.get("/notifications").then((r) => r.data);
export const markNotificationRead = (notifId) =>
  http.post(`/notifications/${notifId}/read`).then((r) => r.data);
export const markAllNotificationsRead = () =>
  http.post("/notifications/read-all").then((r) => r.data);

export const addChild = (payload) => http.post("/auth/me/children", payload).then((r) => r.data.children);
export const removeChild = (childId) =>
  http.delete(`/auth/me/children/${childId}`).then((r) => r.data.children);

export const fetchReports = () => http.get("/admin/reports").then((r) => r.data);
export const resolveReport = (reportId, action) =>
  http.post(`/admin/reports/${reportId}/resolve`, { action }).then((r) => r.data);
export const publishSong = (payload) => http.post("/admin/songs", payload).then((r) => r.data);

export const fetchPlaylists = () => http.get("/me/playlists").then((r) => r.data);
export const createPlaylist = (payload) => http.post("/me/playlists", payload).then((r) => r.data);
export const updatePlaylist = (id, payload) => http.patch(`/me/playlists/${id}`, payload).then((r) => r.data);
export const deletePlaylist = (id) => http.delete(`/me/playlists/${id}`).then((r) => r.data);

export const fetchInvites = () => http.get("/admin/invites").then((r) => r.data);
export const createInvite = (payload) => http.post("/admin/invites", payload).then((r) => r.data);
export const deactivateInvite = (code) => http.post(`/admin/invites/${code}/deactivate`).then((r) => r.data);
export const uploadStoryMedia = async (storyId, file, onProgress) => {
  const up = await uploadChunked(file, onProgress);
  const fd = new FormData();
  fd.append("path", up.path);
  return http.post(`/admin/stories/${storyId}/media`, fd).then((r) => r.data);
};

export const uploadSong = async (title, category, file, onProgress) => {
  const up = await uploadChunked(file, onProgress);
  const fd = new FormData();
  fd.append("title", title);
  fd.append("category", category);
  fd.append("path", up.path);
  return http.post("/admin/songs/upload", fd).then((r) => r.data);
};
export const uploadVideo = async (title, kind, file, onProgress) => {
  const up = await uploadChunked(file, onProgress);
  const fd = new FormData();
  fd.append("title", title);
  fd.append("kind", kind);
  fd.append("path", up.path);
  return http.post("/admin/videos", fd).then((r) => r.data);
};
export const fetchVideos = (kind) =>
  http.get("/videos", { params: kind ? { kind } : {} }).then((r) => r.data);
export const deleteVideo = (id) => http.delete(`/admin/videos/${id}`).then((r) => r.data);

export const forgotPassword = (email) => http.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, password) =>
  http.post("/auth/reset-password", { token, password }).then((r) => r.data);
